import { ref, computed, reactive } from 'vue'
import { defineStore } from 'pinia'
import {
  GENDER,
  type Employee,
  EMPLOYMENT_STATUS,
  SALARY_SCHEME,
  SALARY_CALCULATION,
  MARITAL_STATUS,
  PAY_CYCLE,
  type Company,
  type Department,
  type Position,
  type CostCenter,
} from './types'

import { faker } from '@faker-js/faker'
import dayjs from 'dayjs'
import axios from 'axios'
import { useDepartmentStore } from '@/stores/department'
import { usePositionStore } from '@/stores/position'
import { useCompanyStore } from '@/stores/company'
import { useCostStore } from '@/stores/cost'
import { usePayslipStore } from '@/stores/payslip'
import { useAppStore } from '@/stores/app'
import { useUtil } from '@/composables/util'

import tilt from '../json/employees.json'

const apiURL: string = import.meta.env.VITE_API_URL + '/api'
// const apiURL = "http://localhost:4000/api"

const api = axios.create({
  baseURL: apiURL + '/v1'
})

const my = useUtil()

export const useEmployeeStore = defineStore('employee', () => {

  const departmentStore = useDepartmentStore()
  const positionStore = usePositionStore()
  const companyStore = useCompanyStore()
  const costStore = useCostStore()
  const appStore = useAppStore()

  const employees = reactive<Employee[]>([])
  const selectedEmployee = ref({} as Employee) // selected in table
  const thisEmployee = ref({} as Employee) //for form editing

  const employeeXTD = reactive({} as any)
  const showForm = ref(false)

  function generateEmployees(count = 100) {
    if (employees.length > 100) {
      return
    }
    // return

    for (let index = 0; index < count; index++) {
      const employee = {} as Employee

      employee.uuid = faker.string.uuid()
      employee.employee_id = (index + 1).toString()
      employee.firstname = faker.person.firstName()
      employee.lastname = faker.person.lastName()
      employee.middlename = faker.person.middleName()

      employee.email = faker.internet.email()
      employee.phone = faker.phone.number().split('x')[0]

      employee.street = faker.location.streetAddress()
      employee.address = faker.location.secondaryAddress()
      employee.city = faker.location.city()
      employee.province = faker.location.state()
      employee.zip_code = faker.location.zipCode()

      employee.gender = faker.number.int(10) < 9 ? GENDER.Male : GENDER.Female
      employee.birthdate = faker.date.birthdate()

      employee.employment_status =
        faker.number.int(10) < 9 ? EMPLOYMENT_STATUS.Regular : EMPLOYMENT_STATUS.Probationary
      employee.pay_cycle = PAY_CYCLE.SemiMonthly
      employee.salary_rate = faker.number.float({ min: 400, max: 1500, precision: 2 })
      employee.salary_scheme = SALARY_SCHEME.Daily
      employee.salary_calculation = SALARY_CALCULATION.Daily

      employee.employment_date = faker.date.past()
      employee.marital_status =
        faker.number.int(10) < 5 ? MARITAL_STATUS.Married : MARITAL_STATUS.Single
      employee.sss_number = faker.number.bigInt().toString()
      employee.pagibig_number = faker.number.bigInt().toString()
      employee.philhealth_number = faker.number.bigInt().toString()
      employee.bank_account_number = faker.number.bigInt().toString()

      employee.fullname =
        employee.lastname + ', ' + employee.firstname + ' ' + my.middleInitial(employee.middlename)

      addEmployee(employee)
    }
  }

  function generateTiltEmployees(count = 200) {
    // return

    tilt.forEach((e) => {
      const employee = {} as Employee

      employee.uuid = faker.string.uuid()
      employee.employee_id = e.employee_id
      employee.firstname = e.firstname
      employee.lastname = e.lastname
      employee.middlename = e.middlename

      employee.email = faker.internet.email()
      employee.phone = faker.phone.number().split('x')[0]

      employee.street = faker.location.streetAddress()
      employee.address = faker.location.secondaryAddress()
      employee.city = faker.location.city()
      employee.province = faker.location.state()
      employee.zip_code = faker.location.zipCode()

      employee.gender = faker.number.int(10) < 9 ? GENDER.Male : GENDER.Female
      employee.birthdate = dayjs(e.birthdate).toDate()

      employee.employment_status = EMPLOYMENT_STATUS.Regular
      employee.pay_cycle = PAY_CYCLE.Weekly
      employee.salary_rate = Number(e.salary_rate)
      employee.salary_scheme = SALARY_SCHEME.Daily
      employee.salary_calculation = SALARY_CALCULATION.Daily

      employee.employment_date = dayjs(e.employment_date).toDate()
      employee.marital_status =
        faker.number.int(10) < 5 ? MARITAL_STATUS.Married : MARITAL_STATUS.Single
      employee.sss_number = e.sss_number
      employee.pagibig_number = e.pagibig_number
      employee.philhealth_number = e.philhealth_number
      employee.bank_account_number = faker.number.bigInt().toString()
      employee.fullname = e.lastname + ', ' + e.firstname + ' ' + my.middleInitial(e.middlename)

      addEmployee(employee)
    })
  }

  async function getEmployees() {

    const response = (await api.get('/employees.json'))
    if (response.status == 200) {
      // employees.push(response.data.data)
      employees.length = 0
      response.data.data.forEach((item: Employee) => {
        employees.push(item)
      })
    } else {
    }


  }

  const employeesByPayCycle = computed(() => (paycycle: string) => {
    return employees.filter((item: Employee) => item.pay_cycle == paycycle)

  })


  async function saveEmployee(payload: Employee) {
    if (payload.uuid) {
      const response = (await api.put('/employees/' + payload.uuid, payload))

      if (response.status == 200) {
        const idx = employees.findIndex((item: Employee) => item.uuid == payload.uuid)
        employees.splice(idx, 1, response.data)

        // resync + recalc this employee's payslips in Open periods
        usePayslipStore().dirtyAffectedPayslips({ employee_uuid: payload.uuid })
      } else {
      }
    } else {
      const response = (await api.post('/employees.json', payload))

      if (response.status == 200) {
        employees.push(response.data)
      } else {
      }
    }
  }


  async function addEmployee(payload: Employee) {
    const response = (await api.post('/employees.json', payload))

    if (response.status == 200) {
      employees.push(response.data)
    } else {
    }
  }

  async function deleteEmployee(payload: Employee) {
    const response = await api.delete("/employees/" + payload.uuid);
    // record was actually deleted
    if (response.status == 200) {
      const ndx = employees.findIndex(
        (item: Employee) => item.uuid != payload.uuid
      );
      employees.splice(ndx, 1)
    }
  }

  const importEmployees = async (employees: any) => {


    for (let index = 0; index < employees.length; index++) {
      if (employees[index].uuid == 'uuid') {
        continue
      }
      const { uuid, ...employee } = employees[index]
      // const employee = employees[index]

      // lookup related uuid
      const company = companyStore.companies.find((item: Company) => item.name == employee.company)
      const position = positionStore.positions.find((item: Position) => item.name == employee.position)
      const department = departmentStore.departments.find((item: Department) => item.name == employee.department)
      const cost = costStore.costCenters.find((item: CostCenter) => item.code == employee.cost)


      const payload = {} as Employee

      // Parse date fields - supports M/D/YY, MM/DD/YYYY, YYYY-MM-DD formats
      const employment_date = employee.employment_date
        ? dayjs(employee.employment_date, ['M/D/YY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'M/D/YYYY']).toDate()
        : undefined
      const birthdate = employee.birthdate
        ? dayjs(employee.birthdate, ['M/D/YY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'M/D/YYYY']).toDate()
        : undefined
      const separation_date = employee.separation_date
        ? dayjs(employee.separation_date, ['M/D/YY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'M/D/YYYY']).toDate()
        : undefined

      Object.assign(payload, {
        ...employee,
        employment_date,
        birthdate,
        separation_date,
        company_uuid: company ? company.uuid : '',
        department_uuid: department ? department.uuid : '',
        position_uuid: position ? position.uuid : '',
        cost_uuid: cost ? cost.uuid : ''
      })
      await saveEmployee(payload)
    }
  }

  const fullName = computed(() => (uuid: string) => {
    const employee = employees.find((item: Employee) => item.uuid == uuid)
    if (employee != undefined) {
      return employee?.lastname + ', ' + employee?.firstname + ' ' + my.middleInitial(employee?.middlename) + '. '
    } else {
      return 'NO NAME'
    }
  })

  const id = computed(() => (uuid: string) => {
    const employee = employees.find((item: Employee) => item.uuid == uuid)
    if (employee != undefined) {
      return employee?.employee_id
    } else {
      return 'NO ID'
    }
  })


  const hasMissingFields = computed(() => (employee: any) => {
    if (!employee['schedule_uuid']
      || !employee['department_uuid']
      || !employee['position_uuid']
      || (!employee['company_uuid'] && appStore.options.companyEnabled)
      || (!employee['cost_uuid'] && appStore.options.costEnabled)
      || !employee['salary_rate']
      || !employee['salary_scheme']
      || !employee['salary_calculation']
    ) {
      return true
    } else {
      return false
    }
  })

  const someEmployeesHasMissingFields = computed(() => (paycycle: string) => {
    const someEmployees = employees.filter((item: Employee) => item.pay_cycle == paycycle).filter((employee: Employee) => {
      if (!employee['schedule_uuid']
        || !employee['department_uuid']
        || !employee['position_uuid']
        || (!employee['company_uuid'] && appStore.options.companyEnabled)
        || (!employee['cost_uuid'] && appStore.options.costEnabled)

        || !employee['salary_rate']
        || !employee['salary_scheme']
        || !employee['salary_calculation']
      ) {
        return true
      } else {
        return false
      }


    }) as Array<Employee>

    return someEmployees.length > 0

  })


  // employee MTD and YTD (does not include current date)
  const getEmployeeXTD = async (uuid: string, date: string) => {
    const response = await api.get('/xtd/' + uuid + '?date=' + date.split(' ')[0])
    if (response.status == 200) {
      // employees.push(response.data.data)
      Object.assign(employeeXTD, response.data)
    } else {
    }
    return employeeXTD.value
  }

  return {
    showForm,
    employees,
    selectedEmployee,
    thisEmployee,
    employeeXTD,
    // generateEmployees,
    // generateTiltEmployees,
    deleteEmployee,
    addEmployee,
    saveEmployee,
    getEmployees,
    getEmployeeXTD,
    importEmployees,

    employeesByPayCycle,
    fullName,
    id,
    hasMissingFields,
    someEmployeesHasMissingFields
  }
})
